import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/i18n/LanguageContext';
import { API } from '@/lib/config';
import { Container, SectionKicker, SectionHeading, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { GraduationCap, Stethoscope, Scale, FlaskConical, Landmark, Rocket, Users2, Cpu } from 'lucide-react';

const BACKGROUNDS = [
  { value: 'upsc_aspirant', Icon: Landmark, label_key: 'aspirant.list_1' },
  { value: 'mpsc_aspirant', Icon: Landmark, label_key: 'aspirant.list_1' },
  { value: 'engineer', Icon: Cpu, label_key: 'aspirant.list_2' },
  { value: 'doctor', Icon: Stethoscope, label_key: 'aspirant.list_3' },
  { value: 'lawyer', Icon: Scale, label_key: 'aspirant.list_4' },
  { value: 'academic', Icon: FlaskConical, label_key: 'aspirant.list_5' },
  { value: 'entrepreneur', Icon: Rocket, label_key: 'aspirant.list_6' },
  { value: 'retired_administrator', Icon: Users2, label_key: 'aspirant.list_7' },
  { value: 'young_professional', Icon: GraduationCap, label_key: 'aspirant.list_8' },
  { value: 'other', Icon: Users2, label_key: 'aspirant.list_8' },
];

const BG_LABELS = [
  { key: 'list_1', Icon: Landmark },
  { key: 'list_2', Icon: Cpu },
  { key: 'list_3', Icon: Stethoscope },
  { key: 'list_4', Icon: Scale },
  { key: 'list_5', Icon: FlaskConical },
  { key: 'list_6', Icon: Rocket },
  { key: 'list_7', Icon: Users2 },
  { key: 'list_8', Icon: GraduationCap },
];

function bgOptions(t) {
  return [
    { value: 'upsc_aspirant', label: 'UPSC Aspirant' },
    { value: 'mpsc_aspirant', label: 'MPSC Aspirant' },
    { value: 'engineer', label: t('aspirant.list_2') },
    { value: 'doctor', label: t('aspirant.list_3') },
    { value: 'lawyer', label: t('aspirant.list_4') },
    { value: 'academic', label: t('aspirant.list_5') },
    { value: 'entrepreneur', label: t('aspirant.list_6') },
    { value: 'retired_administrator', label: t('aspirant.list_7') },
    { value: 'young_professional', label: t('aspirant.list_8') },
    { value: 'other', label: 'Other' },
  ];
}

export default function AspirantPage() {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: '', mobile: '', email: '', state: '', district: '',
    background_type: '', current_role: '', expertise_area: '', why_join: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/aspirants`, form);
      toast.success(res.data.message || t('aspirant.success'));
      setForm({
        full_name: '', mobile: '', email: '', state: '', district: '',
        background_type: '', current_role: '', expertise_area: '', why_join: '',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join(', ')
        : (typeof detail === 'string' ? detail : 'Please check your inputs and try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-16">
          <SectionKicker>{t('nav.aspirant')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
            {t('aspirant.title')}
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-[#062B5C] font-medium">{t('aspirant.lede')}</p>
          <p className="mt-2 max-w-3xl text-[#5B6B82] leading-relaxed">{t('aspirant.description')}</p>
        </Container>
      </section>

      <section className="py-14">
        <Container>
          <SectionHeading title={t('aspirant.who_h')} />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4">
            {BG_LABELS.map(({ key, Icon }) => (
              <div key={key} className="rounded-xl border border-[#D7E1EF] bg-white p-4">
                <div className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="mt-3 text-sm font-medium text-[#0B1220]">{t(`aspirant.${key}`)}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-14 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading title={t('aspirant.form_h')} subtitle={t('aspirant.form_sub')} />
          <form
            onSubmit={onSubmit}
            data-testid="aspirant-application-form"
            className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl"
          >
            <FormField label="Full Name" id="asp-name" required>
              <Input id="asp-name" value={form.full_name} onChange={update('full_name')} data-testid="aspirant-full-name" required minLength={2} />
            </FormField>
            <FormField label="Mobile Number" id="asp-mobile" required>
              <Input id="asp-mobile" value={form.mobile} onChange={update('mobile')} data-testid="aspirant-mobile" required />
            </FormField>
            <FormField label="Email" id="asp-email" required>
              <Input id="asp-email" type="email" value={form.email} onChange={update('email')} data-testid="aspirant-email" required />
            </FormField>
            <FormField label="State" id="asp-state" required>
              <Input id="asp-state" value={form.state} onChange={update('state')} data-testid="aspirant-state" required />
            </FormField>
            <FormField label="District" id="asp-district" required>
              <Input id="asp-district" value={form.district} onChange={update('district')} data-testid="aspirant-district" required />
            </FormField>
            <FormField label="Your Background" id="asp-bg" required>
              <Select value={form.background_type} onValueChange={(v) => setForm((s) => ({ ...s, background_type: v }))}>
                <SelectTrigger id="asp-bg" data-testid="aspirant-background" className="bg-white">
                  <SelectValue placeholder="Select your background" />
                </SelectTrigger>
                <SelectContent>
                  {bgOptions(t).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Current Role / Studies" id="asp-role" required>
              <Input id="asp-role" value={form.current_role} onChange={update('current_role')} data-testid="aspirant-current-role" required />
            </FormField>
            <FormField label="Area of Expertise / Interest" id="asp-exp" required>
              <Input id="asp-exp" value={form.expertise_area} onChange={update('expertise_area')} data-testid="aspirant-expertise" required />
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Why do you want to lead with NGRP?" id="asp-why" required>
                <Textarea id="asp-why" rows={5} value={form.why_join} onChange={update('why_join')} data-testid="aspirant-why" required minLength={10} />
              </FormField>
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={submitting} className="bg-[#062B5C] text-white hover:bg-[#05244d]" data-testid="aspirant-form-submit-button">
                {submitting ? t('common.submitting') : t('common.apply_cta')}
              </Button>
            </div>
          </form>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function FormField({ label, id, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-[#0B1220]">
        {label} {required && <span className="text-[#B42318]">*</span>}
      </Label>
      {children}
    </div>
  );
}
